'use client';

import React from 'react';
import {
  Box,
  Grid,
  GridCol,
  Text,
  TextInput,
  Textarea,
  Checkbox,
  Loader,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import Button from '@/components/Button';
import { useResponsive } from '@/hooks/use-responsive';

interface ContactFormProps {
  onSubmitSuccess?: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSubmitSuccess }) => {
  const { responsive } = useResponsive();
  const [isSent, setIsSent] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState('');

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      reason: '',
      message: '',
      termsOfService: false,
    },

    validate: {
      firstName: (value) => !value && 'This field is required',
      lastName: (value) => !value && 'This field is required',
      email: (value) => {
        if (!value) return 'This field is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email';
      },
      phone: (value) => {
        if (value && !/^\+?(\d[\d\- ]{7,}\d)$/.test(value))
          return 'Invalid phone number';
      },
      reason: (value) => !value && 'This field is required',
      message: (value) => !value && 'This field is required',
      termsOfService: (value) =>
        !value && 'You must agree to the terms and conditions',
    },
  });

  const handleFormSubmit = async (values: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    reason: string;
    message: string;
    termsOfService: boolean;
  }) => {
    const controller = new AbortController();
    const signal = controller.signal;

    setIsSubmitting(true);
    setFormError('');

    try {
      const { grecaptcha } = window as any;

      if (grecaptcha) {
        const token = await new Promise<string>((resolve, reject) => {
          grecaptcha.ready(() => {
            grecaptcha
              .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
                action: 'submit',
              })
              .then(resolve)
              .catch(reject);
          });
        });

        const response = await fetch('/api/contact', {
          method: 'POST',
          body: JSON.stringify({
            ...values,
            token,
          }),
          signal,
        });

        if (response.status !== 200) {
          setFormError('An error occurred. Please try again later.');
          setIsSubmitting(false);
          return;
        }

        const responseData = await response.json();

        if (responseData.error) {
          setFormError(responseData.error);
          setIsSubmitting(false);
          return;
        }

        setIsSent(true);
        setIsSubmitting(false);
        
        // Call the optional success callback
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormError('An unexpected error occurred. Please try again later.');
      setIsSubmitting(false);
    }
  };

  if (isSent) {
    return (
      <Box pos="sticky" top={120} w={'100%'}>
        <Title size={42} fw={700} mb={40}>
          Thank you!
        </Title>

        <Text fz={18} c="gray.7" mb={40}>
          We have received your message and will get back to you as soon as possible.
        </Text>
      </Box>
    );
  }

  return (
    <>
      <form onSubmit={form.onSubmit(handleFormSubmit)}>
        <Grid columns={2} gutter={30}>
          <GridCol span={responsive({ xs: 2, sm: 1, lg: 2, xl: 1 })}>
            <TextInput
              label="First name"
              size="lg"
              radius="md"
              disabled={isSubmitting}
              {...form.getInputProps('firstName')}
            />
          </GridCol>

          <GridCol span={responsive({ xs: 2, sm: 1, lg: 2, xl: 1 })}>
            <TextInput
              label="Last name"
              size="lg"
              radius="md"
              disabled={isSubmitting}
              {...form.getInputProps('lastName')}
            />
          </GridCol>

          <GridCol span={responsive({ xs: 2, sm: 1, lg: 2, xl: 1 })}>
            <TextInput
              label="Email address"
              size="lg"
              radius="md"
              disabled={isSubmitting}
              {...form.getInputProps('email')}
            />
          </GridCol>

          <GridCol span={responsive({ xs: 2, sm: 1, lg: 2, xl: 1 })}>
            <TextInput
              label="Phone number"
              size="lg"
              radius="md"
              disabled={isSubmitting}
              {...form.getInputProps('phone')}
            />
          </GridCol>

          <GridCol span={2}>
            <TextInput
              label="How can we help you?"
              size="lg"
              radius="md"
              disabled={isSubmitting}
              {...form.getInputProps('reason')}
            />
          </GridCol>

          <GridCol span={2}>
            <Textarea
              label="Message"
              size="lg"
              radius="md"
              autosize
              minRows={5}
              disabled={isSubmitting}
              {...form.getInputProps('message')}
            />
          </GridCol>

          <GridCol span={2}>
            <Checkbox
              label="By sending this message I agree to the terms and conditions"
              disabled={isSubmitting}
              {...form.getInputProps('termsOfService', {
                type: 'checkbox',
              })}
            />
          </GridCol>

          <GridCol span={2}>
            <Button
              component="button"
              fullwidth
              transition={false}
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? <Loader size={25} /> : 'Send message'}
            </Button>

            {formError && (
              <Text c="red" mt={10}>
                {formError}
              </Text>
            )}
          </GridCol>
        </Grid>
      </form>
    </>
  );
};

export default ContactForm;